import { useState, useEffect } from "react";
import { Layout } from "@/components/layout";
import { useAuth } from "@/contexts/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Settings, Lock, User, Palette, Check, ShieldQuestion, CreditCard, Zap, Crown, ExternalLink, AlertCircle, RefreshCw, Bell, Trash2, PauseCircle, PlayCircle, ShieldCheck, Shield, Mail, Smartphone, Eye, EyeOff, Copy, Download, KeyRound, Info, Calendar, Clock, Timer, ShieldAlert, Type, CaseSensitive, Sparkles, LayoutGrid } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useTheme } from "@/contexts/theme";
import { THEMES } from "@/lib/themes";
import { useLanguage } from "@/contexts/language";
import { useFontScale } from "@/contexts/fontScale";
import { FONT_SCALE_IDS } from "@/lib/fontScale";
import { useFontStyle } from "@/contexts/fontStyle";
import { FONT_STYLE_IDS, FONT_STYLE_PREVIEW_FAMILY } from "@/lib/fontStyle";
import { useStudyComfort } from "@/contexts/studyComfort";
import { useSidebarClock } from "@/contexts/sidebarClock";
import { validateNicknameModeration, NICKNAME_NOT_ALLOWED_MESSAGE } from "@/lib/nicknameModeration";
import { SECURITY_QUESTIONS } from "@/lib/security-questions";
import { cn } from "@/lib/utils";
import { fetchWithAuth } from "@/lib/api";

const PILOT_TYPES = [
  "Student Pilot Permit – Aeroplane",
  "Recreational Pilot Permit – Aeroplane",
  "Private Pilot Licence – Aeroplane (PPL-A)",
  "Commercial Pilot Licence – Aeroplane (CPL-A)",
  "Airline Transport Pilot Licence – Aeroplane (ATPL-A)",
  "Student Pilot Permit – Helicopter",
  "Private Pilot Licence – Helicopter (PPL-H)",
  "Commercial Pilot Licence – Helicopter (CPL-H)",
  "Airline Transport Pilot Licence – Helicopter (ATPL-H)",
  "Student Pilot Permit – Gyroplane",
  "Gyroplane Pilot Licence",
  "Student Pilot Permit – Balloon",
  "Balloon Pilot Licence",
  "Student Pilot Permit – Glider",
  "Glider Pilot Licence",
  "Ultra-light Aeroplane Pilot Permit",
  "Drone Pilot Certificate – Basic",
  "Drone Pilot Certificate – Advanced",
  "None / Aspiring",
];

const RATINGS = [
  "Night Rating", "IFR Rating", "Multi-Engine Land", "Multi-Engine Sea",
  "Float Plane", "Tailwheel", "Mountain Flying",
  "Instructor (FI)", "Instructor (IRI)", "Aerobatics",
];

const GENDERS = [
  { value: "", label: "Prefer not to say" },
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "other", label: "Other" },
];

const COUNTRIES = [
  "Afghanistan","Albania","Algeria","Andorra","Angola","Antigua and Barbuda","Argentina","Armenia","Australia","Austria",
  "Azerbaijan","Bahamas","Bahrain","Bangladesh","Barbados","Belarus","Belgium","Belize","Benin","Bhutan",
  "Bolivia","Bosnia and Herzegovina","Botswana","Brazil","Brunei","Bulgaria","Burkina Faso","Burundi","Cabo Verde","Cambodia",
  "Cameroon","Canada","Central African Republic","Chad","Chile","China","Colombia","Comoros","Congo (DRC)","Congo (Republic)",
  "Costa Rica","Croatia","Cuba","Cyprus","Czech Republic","Denmark","Djibouti","Dominica","Dominican Republic","Ecuador",
  "Egypt","El Salvador","Equatorial Guinea","Eritrea","Estonia","Eswatini","Ethiopia","Fiji","Finland","France",
  "Gabon","Gambia","Georgia","Germany","Ghana","Greece","Grenada","Guatemala","Guinea","Guinea-Bissau",
  "Guyana","Haiti","Honduras","Hungary","Iceland","India","Indonesia","Iran","Iraq","Ireland",
  "Israel","Italy","Jamaica","Japan","Jordan","Kazakhstan","Kenya","Kiribati","Kuwait","Kyrgyzstan",
  "Laos","Latvia","Lebanon","Lesotho","Liberia","Libya","Liechtenstein","Lithuania","Luxembourg","Madagascar",
  "Malawi","Malaysia","Maldives","Mali","Malta","Marshall Islands","Mauritania","Mauritius","Mexico","Micronesia",
  "Moldova","Monaco","Mongolia","Montenegro","Morocco","Mozambique","Myanmar","Namibia","Nauru","Nepal",
  "Netherlands","New Zealand","Nicaragua","Niger","Nigeria","North Korea","North Macedonia","Norway","Oman","Pakistan",
  "Palau","Panama","Papua New Guinea","Paraguay","Peru","Philippines","Poland","Portugal","Qatar","Romania",
  "Russia","Rwanda","Saint Kitts and Nevis","Saint Lucia","Saint Vincent and the Grenadines","Samoa","San Marino","Saudi Arabia","Senegal","Serbia",
  "Seychelles","Sierra Leone","Singapore","Slovakia","Slovenia","Solomon Islands","Somalia","South Africa","South Korea","South Sudan",
  "Spain","Sri Lanka","Sudan","Suriname","Sweden","Switzerland","Syria","Taiwan","Tajikistan","Tanzania",
  "Thailand","Timor-Leste","Togo","Tonga","Trinidad and Tobago","Tunisia","Turkey","Turkmenistan","Tuvalu","Uganda",
  "Ukraine","United Arab Emirates","United Kingdom","United States","Uruguay","Uzbekistan","Vanuatu","Vatican City","Venezuela","Vietnam",
  "Yemen","Zambia","Zimbabwe",
];

type Tab = "appearance" | "account" | "security" | "billing";

interface SubscriptionDetails {
  id: string;
  status: string;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: number;
  currentPeriodStart: number;
  interval: "month" | "year" | null;
  intervalCount: number;
  amount: number | null;
  currency: string;
}

function PlanBadge({ plan }: { plan: string }) {
  if (plan === "pro_plus") return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-mono font-semibold bg-amber-500/20 text-amber-400 border border-amber-500/30">
      <Crown className="w-3 h-3" /> PRO PLUS
    </span>
  );
  if (plan === "pro") return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-mono font-semibold bg-primary/20 text-primary border border-primary/30">
      <Zap className="w-3 h-3" /> PRO
    </span>
  );
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-mono font-semibold bg-secondary text-muted-foreground border border-border">
      BASIC (FREE)
    </span>
  );
}

export default function SettingsPage() {
  const { user, updateUser, set2faSetupDone, needs2faSetup } = useAuth();
  const { toast } = useToast();
  const { theme, setTheme } = useTheme();
  const { t: tl, language, setLanguage } = useLanguage();
  const { fontScale, setFontScale } = useFontScale();
  const { fontStyle, setFontStyle } = useFontStyle();
  const { reduceMotion, comfortableSession, setReduceMotion, setComfortableSession } = useStudyComfort();
  const { showSidebarClock, useLocalTime, setShowSidebarClock, setUseLocalTime } = useSidebarClock();
  const [activeTab, setActiveTab] = useState<Tab>("appearance");

  // Deep-link to 2FA section when needs2faSetup is true or URL hash is #two-factor-auth
  useEffect(() => {
    const shouldJump = needs2faSetup || window.location.hash === "#two-factor-auth";
    if (!shouldJump) return;
    setActiveTab("security");
    const timer = setTimeout(() => {
      const el = document.getElementById("two-factor-auth");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 150);
    return () => clearTimeout(timer);
  }, [needs2faSetup]);

  // Profile form state
  const [fullName, setFullName] = useState(user?.fullName ?? "");
  const [email, setEmail] = useState(user?.email ?? "");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [country, setCountry] = useState("");
  const [gender, setGender] = useState("");
  const [age, setAge] = useState("");
  const [pilotType, setPilotType] = useState("");
  const [ratings, setRatings] = useState<string[]>([]);
  const [nickname, setNickname] = useState("");
  const [leaderboardOptIn, setLeaderboardOptIn] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);

  // Password form state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passLoading, setPassLoading] = useState(false);

  // Security questions state
  const [sq, setSq] = useState(["", "", ""]);
  const [sa, setSa] = useState(["", "", ""]);
  const [sqLoading, setSqLoading] = useState(false);
  const [sqHasExisting, setSqHasExisting] = useState(false);

  // Two-Factor Authentication state
  const [twoFaMethod, setTwoFaMethod] = useState<string | null>(null);
  const [totpSetupPending, setTotpSetupPending] = useState(false);
  const [twoFaLoading, setTwoFaLoading] = useState(false);
  const [twoFaQrDataUrl, setTwoFaQrDataUrl] = useState("");
  const [twoFaTotpSecret, setTwoFaTotpSecret] = useState("");
  const [twoFaVerifyCode, setTwoFaVerifyCode] = useState("");
  const [showTotpSecret, setShowTotpSecret] = useState(false);
  const [twoFaDisablePassword, setTwoFaDisablePassword] = useState("");
  const [showTwoFaDisable, setShowTwoFaDisable] = useState(false);
  // Recovery codes state
  const [recoveryModalCodes, setRecoveryModalCodes] = useState<string[]>([]);
  const [showRecoveryModal, setShowRecoveryModal] = useState(false);
  const [recoveryCodesRemaining, setRecoveryCodesRemaining] = useState(0);
  const [regenLoading, setRegenLoading] = useState(false);

  // Communications preferences state
  const [newsletterOptIn, setNewsletterOptIn] = useState(true);
  const [adminContactOptIn, setAdminContactOptIn] = useState(true);
  const [prefsLoading, setPrefsLoading] = useState(false);
  const [prefsLoaded, setPrefsLoaded] = useState(false);

  // Onboarding tour toggle
  const [tourToggleLoading, setTourToggleLoading] = useState(false);

  async function handleTourToggle(enabled: boolean) {
    setTourToggleLoading(true);
    try {
      await fetchWithAuth("/api/user/onboarding", { method: "PATCH", body: JSON.stringify({ completed: !enabled }) });
      updateUser({ onboardingCompleted: !enabled });
    } catch (err) {
      toast({ title: "Error", description: err instanceof Error ? err.message : "Could not update tour setting", variant: "destructive" });
    } finally {
      setTourToggleLoading(false);
    }
  }

  // Account management state
  const [acctLoading, setAcctLoading] = useState<string | null>(null);
  const [acctStatus, setAcctStatus] = useState<{ paused: boolean; pauseExpiresAt?: string | null } | null>(null);
  const [showPauseConfirm, setShowPauseConfirm] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  // Billing state
  const [billingPlan, setBillingPlan] = useState<string>("free");
  const [subDetails, setSubDetails] = useState<SubscriptionDetails | null>(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);

  // Load full profile on mount
  useEffect(() => {
    fetchWithAuth("/api/user/profile").then((data) => {
      setFullName(data.fullName ?? "");
      setEmail(data.email ?? "");
      setAddress(data.address ?? "");
      setPhone(data.phone ?? "");
      setCountry(data.country ?? "");
      setGender(data.gender ?? "");
      setAge(data.age != null ? String(data.age) : "");
      setPilotType(data.pilotType ?? "");
      setRatings(Array.isArray(data.ratings) ? data.ratings : []);
      setNickname(typeof data.nickname === "string" ? data.nickname : "");
      setLeaderboardOptIn(data.leaderboardOptIn !== false);
    }).catch(() => {});

    fetchWithAuth("/api/user/account-status").then((data) => {
      setNewsletterOptIn(!!data.newsletterOptIn);
      setAdminContactOptIn(!!data.adminContactOptIn);
      setPrefsLoaded(true);
      setAcctStatus({ paused: !!(data.isPaused ?? data.paused), pauseExpiresAt: data.pauseExpiresAt });
    }).catch(() => { setPrefsLoaded(true); });

    fetchWithAuth("/api/auth/security-questions/me").then((data) => {
      if (data.question1 && data.question2 && data.question3) {
        setSq([data.question1, data.question2, data.question3]);
        setSa(["", "", ""]);
        setSqHasExisting(true);
      }
    }).catch(() => {});

    fetchWithAuth("/api/auth/2fa/status").then((data) => {
      setTwoFaMethod(data.method ?? null);
      setTotpSetupPending(!!data.totpSetupPending);
      setRecoveryCodesRemaining(data.recoveryCodesRemaining ?? 0);
    }).catch(() => {});

    loadBilling();
  }, []);

  async function savePreferences(opts: { newsletter?: boolean; adminContact?: boolean }) {
    setPrefsLoading(true);
    try {
      await fetchWithAuth("/api/user/preferences", {
        method: "PATCH",
        body: JSON.stringify({
          newsletterOptIn: opts.newsletter !== undefined ? opts.newsletter : newsletterOptIn,
          adminContactOptIn: opts.adminContact !== undefined ? opts.adminContact : adminContactOptIn,
        }),
      });
      toast({ title: "Saved", description: "Communication preferences updated." });
    } catch (err: unknown) {
      toast({ title: "Error", description: err instanceof Error ? err.message : "Could not save preferences", variant: "destructive" });
    } finally {
      setPrefsLoading(false);
    }
  }

  async function pauseAccount() {
    setAcctLoading("pause");
    try {
      const data = await fetchWithAuth("/api/user/account/pause", { method: "POST", body: JSON.stringify({ days: 30 }) });
      setAcctStatus({ paused: true, pauseExpiresAt: data.pauseExpiresAt ?? data.expiresAt ?? null });
      toast({ title: "Account paused", description: "Your subscription is paused for up to 30 days." });
    } catch (err: unknown) {
      toast({ title: "Error", description: err instanceof Error ? err.message : "Could not pause account", variant: "destructive" });
    } finally {
      setAcctLoading(null);
    }
  }


  async function resumeAccount() {
    setAcctLoading("resume");
    try {
      await fetchWithAuth("/api/user/account/resume", { method: "POST" });
      setAcctStatus({ paused: false });
      toast({ title: "Resumed", description: "Your subscription has been reactivated." });
    } catch (err: unknown) {
      toast({ title: "Error", description: err instanceof Error ? err.message : "Could not resume account", variant: "destructive" });
    } finally {
      setAcctLoading(null);
    }
  }

  async function resetStats() {
    if (resetConfirmText !== "RESET") return;
    setAcctLoading("reset");
    try {
      await fetchWithAuth("/api/user/stats", { method: "DELETE" });
      setShowResetConfirm(false);
      setResetConfirmText("");
      toast({ title: "Stats cleared", description: "All practice sessions and flight log entries have been permanently deleted." });
    } catch (err: unknown) {
      toast({ title: "Error", description: err instanceof Error ? err.message : "Could not reset stats", variant: "destructive" });
    } finally {
      setAcctLoading(null);
    }
  }

  async function deleteAccount() {
    if (deleteConfirmText !== "DELETE") return;
    setAcctLoading("delete");
    try {
      await fetchWithAuth("/api/user/account", { method: "DELETE" });
      toast({ title: "Account deleted", description: "Your account has been deactivated. You will be signed out." });
      setTimeout(() => { window.location.href = "/"; }, 2000);
    } catch (err: unknown) {
      toast({ title: "Error", description: err instanceof Error ? err.message : "Could not delete account", variant: "destructive" });
    } finally {
      setAcctLoading(null);
    }
  }

  async function loadBilling() {
    setBillingLoading(true);
    try {
      const [subData, detailsData] = await Promise.all([
        fetchWithAuth("/api/stripe/subscription"),
        fetchWithAuth("/api/stripe/subscription-details"),
      ]);
      setBillingPlan(subData.plans?.cpl ?? subData.plan ?? "free");
      setSubDetails(detailsData.subscription ?? null);
    } catch (err) {
      console.warn("[settings] Failed to load billing info:", err);
    } finally {
      setBillingLoading(false);
    }
  }

  function safeStripeRedirect(url: string) {
    const parsed = new URL(url);
    const allowed = ["checkout.stripe.com", "billing.stripe.com"];
    if (!allowed.includes(parsed.hostname)) throw new Error("Unexpected redirect URL from payment provider");
    window.location.href = parsed.toString();
  }

  async function openPortal() {
    setPortalLoading(true);
    try {
      const returnUrl = window.location.href;
      const data = await fetchWithAuth("/api/stripe/portal", {
        method: "POST",
        body: JSON.stringify({ returnUrl }),
      });
      safeStripeRedirect(data.url);
    } catch (err: unknown) {
      toast({ title: "Error", description: err instanceof Error ? err.message : "Could not open billing portal", variant: "destructive" });
    } finally {
      setPortalLoading(false);
    }
  }

  async function startCheckout(plan: "pro" | "pro_plus", interval: "month" | "year") {
    const key = `${plan}-${interval}`;
    setCheckoutLoading(key);
    try {
      const successUrl = `${window.location.origin}/settings`;
      const cancelUrl = window.location.href;
      const data = await fetchWithAuth("/api/stripe/checkout", {
        method: "POST",
        body: JSON.stringify({ plan, track: "cpl", interval, successUrl, cancelUrl }),
      });
      safeStripeRedirect(data.url);
    } catch (err: unknown) {
      toast({ title: "Error", description: err instanceof Error ? err.message : "Could not start checkout", variant: "destructive" });
    } finally {
      setCheckoutLoading(null);
    }
  }

  async function handleSecurityQuestionsSave(e: React.FormEvent) {
    e.preventDefault();
    if (sq.some(q => !q) || sa.some(a => !a.trim())) {
      toast({ title: "Incomplete", description: "Please select all 3 questions and fill in all answers.", variant: "destructive" });
      return;
    }
    if (new Set(sq).size < 3) {
      toast({ title: "Duplicate questions", description: "Please choose 3 different security questions.", variant: "destructive" });
      return;
    }
    setSqLoading(true);
    try {
      await fetchWithAuth("/api/auth/security-questions", {
        method: "POST",
        body: JSON.stringify({
          question1: sq[0], answer1: sa[0],
          question2: sq[1], answer2: sa[1],
          question3: sq[2], answer3: sa[2],
        }),
      });
      setSqHasExisting(true);
      setSa(["", "", ""]);
      toast({ title: "Security questions saved", description: "Your recovery questions have been updated." });
    } catch (err: unknown) {
      toast({ title: "Error", description: err instanceof Error ? err.message : "Failed to save questions", variant: "destructive" });
    } finally {
      setSqLoading(false);
    }
  }

  async function handleProfileSave(e: React.FormEvent) {
    e.preventDefault();
    const trimmedNick = nickname.trim();
    if (trimmedNick.length > 0) {
      if (trimmedNick.length < 2 || trimmedNick.length > 24) {
        toast({ title: "Invalid nickname", description: "Nickname must be 2–24 characters.", variant: "destructive" });
        return;
      }
      if (!/^[a-zA-Z0-9 _-]+$/.test(trimmedNick)) {
        toast({
          title: "Invalid nickname",
          description: "Use only letters, numbers, spaces, underscore, or hyphen.",
          variant: "destructive",
        });
        return;
      }
      if (!validateNicknameModeration(trimmedNick).ok) {
        toast({ title: "Nickname not allowed", description: NICKNAME_NOT_ALLOWED_MESSAGE, variant: "destructive" });
        return;
      }
    }
    setProfileLoading(true);
    try {
      const payload: Record<string, unknown> = {
        fullName,
        email,
        address,
        phone: phone || null,
        country: country || null,
        gender,
        pilotType: pilotType || null,
        ratings,
        nickname: trimmedNick === "" ? null : trimmedNick,
        leaderboardOptIn,
      };
      if (age !== "") payload.age = parseInt(age, 10);
      else payload.age = null;

      const updated = await fetchWithAuth("/api/user/profile", {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
      updateUser({
        fullName: updated.fullName,
        email: updated.email,
        nickname: updated.nickname ?? null,
        leaderboardOptIn: updated.leaderboardOptIn !== false,
      });
      toast({ title: "Profile updated", description: "Your details have been saved." });
    } catch (err: unknown) {
      toast({ title: "Error", description: err instanceof Error ? err.message : "Failed to update profile", variant: "destructive" });
    } finally {
      setProfileLoading(false);
    }
  }

  async function handlePasswordSave(e: React.FormEvent) {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast({ title: "Passwords don't match", description: "New password and confirmation must match.", variant: "destructive" });
      return;
    }
    if (newPassword.length < 8) {
      toast({ title: "Password too short", description: "New password must be at least 8 characters.", variant: "destructive" });
      return;
    }
    setPassLoading(true);
    try {
      await fetchWithAuth("/api/user/password", {
        method: "PATCH",
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      toast({ title: "Password changed", description: "Your password has been updated." });
    } catch (err: unknown) {
      toast({ title: "Error", description: err instanceof Error ? err.message : "Failed to change password", variant: "destructive" });
    } finally {
      setPassLoading(false);
    }
  }

  // ── 2FA handlers ─────────────────────────────────────────────────────────────
  async function startTotpSetup() {
    setTwoFaLoading(true);
    try {
      const data = await fetchWithAuth("/api/auth/2fa/totp/setup", { method: "POST" });
      setTwoFaQrDataUrl(data.qrDataUrl);
      setTwoFaTotpSecret(data.secret);
      setTotpSetupPending(true);
      setTwoFaVerifyCode("");
    } catch (err: unknown) {
      toast({ title: "Error", description: err instanceof Error ? err.message : "Could not start TOTP setup", variant: "destructive" });
    } finally {
      setTwoFaLoading(false);
    }
  }

  async function cancelTotpSetup() {
    setTwoFaLoading(true);
    try {
      await fetchWithAuth("/api/auth/2fa/totp/cancel-setup", { method: "POST" });
      setTotpSetupPending(false);
      setTwoFaQrDataUrl("");
      setTwoFaTotpSecret("");
      setTwoFaVerifyCode("");
    } catch (err: unknown) {
      toast({ title: "Error", description: err instanceof Error ? err.message : "Could not cancel", variant: "destructive" });
    } finally {
      setTwoFaLoading(false);
    }
  }

  async function verifyTotpSetup() {
    if (!twoFaVerifyCode.trim()) return;
    setTwoFaLoading(true);
    try {
      const data = await fetchWithAuth("/api/auth/2fa/totp/verify-setup", { method: "POST", body: JSON.stringify({ code: twoFaVerifyCode.trim() }) });
      setTwoFaMethod("totp");
      setTotpSetupPending(false);
      setTwoFaQrDataUrl("");
      setTwoFaTotpSecret("");
      setTwoFaVerifyCode("");
      set2faSetupDone();
      if (Array.isArray(data.recoveryCodes) && data.recoveryCodes.length > 0) {
        setRecoveryModalCodes(data.recoveryCodes);
        setRecoveryCodesRemaining(data.recoveryCodes.length);
        setShowRecoveryModal(true);
      } else {
        toast({ title: "Authenticator enabled", description: "Your account is now protected by two-factor authentication." });
      }
    } catch (err: unknown) {
      toast({ title: "Invalid code", description: err instanceof Error ? err.message : "Verification failed", variant: "destructive" });
    } finally {
      setTwoFaLoading(false);
    }
  }

  async function enableEmailOtp() {
    setTwoFaLoading(true);
    try {
      const data = await fetchWithAuth("/api/auth/2fa/email/enable", { method: "POST" });
      setTwoFaMethod("email");
      set2faSetupDone();
      if (Array.isArray(data.recoveryCodes) && data.recoveryCodes.length > 0) {
        setRecoveryModalCodes(data.recoveryCodes);
        setRecoveryCodesRemaining(data.recoveryCodes.length);
        setShowRecoveryModal(true);
      } else {
        toast({ title: "Email OTP enabled", description: "You will receive a code each time you log in." });
      }
    } catch (err: unknown) {
      toast({ title: "Error", description: err instanceof Error ? err.message : "Could not enable email OTP", variant: "destructive" });
    } finally {
      setTwoFaLoading(false);
    }
  }

  async function regenerateRecoveryCodes() {
    setRegenLoading(true);
    try {
      const data = await fetchWithAuth("/api/auth/2fa/recovery-codes/regenerate", { method: "POST" });
      if (Array.isArray(data.recoveryCodes)) {
        setRecoveryModalCodes(data.recoveryCodes);
        setRecoveryCodesRemaining(data.recoveryCodes.length);
        setShowRecoveryModal(true);
      }
    } catch (err: unknown) {
      toast({ title: "Error", description: err instanceof Error ? err.message : "Could not regenerate codes", variant: "destructive" });
    } finally {
      setRegenLoading(false);
    }
  }

  function copyRecoveryCodes() {
    navigator.clipboard.writeText(recoveryModalCodes.join("\n")).then(() => {
      toast({ title: "Copied", description: "Recovery codes copied to clipboard." });
    }).catch(() => {
      toast({ title: "Error", description: "Could not copy to clipboard.", variant: "destructive" });
    });
  }

  function downloadRecoveryCodes() {
    const content = [
      "Flight Deck — Recovery Codes",
      "Keep these codes somewhere safe. Each code can only be used once.",
      "",
      ...recoveryModalCodes,
    ].join("\n");
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "flightdeck-recovery-codes.txt";
    a.click();
    URL.revokeObjectURL(url);
  }

  async function disable2FA() {
    if (!twoFaDisablePassword.trim()) return;
    setTwoFaLoading(true);
    try {
      await fetchWithAuth("/api/auth/2fa/disable", { method: "POST", body: JSON.stringify({ password: twoFaDisablePassword }) });
      setTwoFaMethod(null);
      setTotpSetupPending(false);
      setTwoFaQrDataUrl("");
      setTwoFaTotpSecret("");
      setShowTwoFaDisable(false);
      setTwoFaDisablePassword("");
      setRecoveryCodesRemaining(0);
      toast({ title: "2FA disabled", description: "Two-factor authentication has been removed from your account." });
    } catch (err: unknown) {
      toast({ title: "Error", description: err instanceof Error ? err.message : "Could not disable 2FA", variant: "destructive" });
    } finally {
      setTwoFaLoading(false);
    }
  }

  const tabs: { key: Tab; label: string; icon: React.ReactNode }[] = [
    { key: "appearance", label: tl.settings.tabs.appearance, icon: <Palette className="h-4 w-4" /> },
    { key: "account", label: tl.settings.tabs.account, icon: <User className="h-4 w-4" /> },
    { key: "security", label: tl.settings.tabs.security, icon: <Shield className="h-4 w-4" /> },
    { key: "billing", label: tl.settings.tabs.billing, icon: <CreditCard className="h-4 w-4" /> },
  ];

  return (
    <>
    {/* Recovery Codes Modal */}
    {showRecoveryModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
        <div className="bg-[#0a0e1a] border border-primary/40 rounded-lg max-w-md w-full shadow-2xl">
          <div className="p-6 space-y-4">
            <div className="flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-primary" />
              <h2 className="text-base font-bold font-mono uppercase tracking-widest text-primary">{tl.settings.recovery.title}</h2>
            </div>
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-md px-4 py-3">
              <p className="text-xs font-mono text-amber-300 leading-relaxed">
                {tl.settings.recovery.hint}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {recoveryModalCodes.map((code) => (
                <code key={code} className="text-center text-sm font-mono bg-secondary/50 border border-border rounded px-3 py-2 text-foreground tracking-widest">
                  {code}
                </code>
              ))}
            </div>
            <div className="flex gap-2 pt-1">
              <Button size="sm" variant="outline" onClick={copyRecoveryCodes} className="font-mono text-xs uppercase tracking-widest gap-1.5 flex-1">
                <Copy className="h-3.5 w-3.5" /> {tl.settings.recovery.copyAll}
              </Button>
              <Button size="sm" variant="outline" onClick={downloadRecoveryCodes} className="font-mono text-xs uppercase tracking-widest gap-1.5 flex-1">
                <Download className="h-3.5 w-3.5" /> {tl.settings.recovery.download}
              </Button>
            </div>
            <Button
              onClick={() => setShowRecoveryModal(false)}
              className="w-full font-mono text-xs uppercase tracking-widest"
            >
              {tl.settings.recovery.savedCodes}
            </Button>
          </div>
        </div>
      </div>
    )}
    <Layout>
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <div className="flex items-center gap-3 mb-1">
            <Settings className="h-5 w-5 text-primary" />
            <h1 className="text-2xl font-bold tracking-tight">{tl.settings.title.toUpperCase()}</h1>
          </div>
          <p className="text-sm text-muted-foreground font-mono">{tl.settings.headerSub}</p>
        </div>

        {/* Tab bar */}
        <div className="flex flex-wrap gap-1 border-b border-border">
          {tabs.map(t => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={cn(
                "flex items-center gap-2 px-4 py-2.5 text-sm font-mono font-medium border-b-2 -mb-px transition-colors",
                activeTab === t.key
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
              )}
            >
              {t.icon}
              {t.label}
            </button>
          ))}
        </div>

        {/* ── TAB 1: APPEARANCE ── */}
        {activeTab === "appearance" && (
          <Card className="border border-border bg-card">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 mb-5">
                <Palette className="h-4 w-4 text-primary" />
                <h2 className="text-sm font-bold font-mono uppercase tracking-widest text-muted-foreground">{tl.settings.appearance.themeLabel}</h2>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {THEMES.map((t) => {
                  const active = theme === t.id;
                  const isRounded =
                    t.style === "sans-rounded" || t.style === "sans-rounded-xl" || t.style === "serif-warm" || t.style === "neo-3d";
                  const isXlRounded = t.style === "sans-rounded-xl" || t.style === "serif-warm" || t.style === "neo-3d";
                  const borderRadius = isXlRounded ? "16px" : isRounded ? "8px" : "2px";
                  const pillRadius = isXlRounded ? "9999px" : isRounded ? "9999px" : "1px";
                  const cardRadius = isXlRounded ? "12px" : isRounded ? "6px" : "1px";
                  return (
                    <button
                      key={t.id}
                      onClick={() => setTheme(t.id)}
                      className={cn(
                        "relative flex flex-col gap-3 rounded-md border p-4 text-left transition-all",
                        active
                          ? "border-primary bg-primary/10 ring-1 ring-primary/40"
                          : "border-border bg-secondary/30 hover:bg-secondary/60"
                      )}
                    >
                      {/* Mini UI preview */}
                      <div
                        className="w-full h-16 rounded flex flex-col gap-1 p-2 overflow-hidden flex-shrink-0"
                        style={{ background: t.bg, borderRadius }}
                      >
                        {/* Fake nav bar */}
                        <div className="flex items-center gap-1 mb-0.5">
                          <div style={{ width: 24, height: 4, background: t.primary, borderRadius: pillRadius, opacity: 0.9 }} />
                          <div style={{ width: 14, height: 4, background: t.primary, borderRadius: pillRadius, opacity: 0.4 }} />
                          <div style={{ width: 14, height: 4, background: t.primary, borderRadius: pillRadius, opacity: 0.4 }} />
                        </div>
                        {/* Fake card */}
                        <div style={{ background: t.mode === "light" ? "rgba(255,255,255,0.85)" : "rgba(255,255,255,0.07)", borderRadius: cardRadius, padding: "4px 6px" }}>
                          <div style={{ width: "60%", height: 3, background: t.primary, borderRadius: pillRadius, marginBottom: 3 }} />
                          <div style={{ width: "40%", height: 2, background: t.mode === "light" ? "rgba(0,0,0,0.25)" : "rgba(255,255,255,0.3)", borderRadius: pillRadius }} />
                        </div>
                        {/* Fake button */}
                        <div style={{ background: t.primary, borderRadius: pillRadius, height: 10, width: 36, opacity: 0.9 }} />
                      </div>

                      {/* Label + check */}
                      <div className="flex items-start justify-between gap-1">
                        <div>
                          <div className="text-xs font-bold font-mono uppercase tracking-widest" style={{ color: active ? t.primary : undefined }}>
                            {t.label}
                          </div>
                          <div className="text-[11px] text-muted-foreground font-mono mt-0.5">{t.description}</div>
                        </div>
                        {active && (
                          <Check className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" style={{ color: t.primary }} />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Text size */}
              <div className="mt-6 border-t border-border pt-6">
                <div className="flex items-center gap-2 mb-4">
                  <Type className="h-4 w-4 text-primary" />
                  <h2 className="text-sm font-bold font-mono uppercase tracking-widest text-muted-foreground">
                    {tl.settings.appearance.fontSizeLabel}
                  </h2>
                </div>
                <p className="text-xs text-muted-foreground font-mono mb-4">{tl.settings.appearance.fontSizeDesc}</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                  {FONT_SCALE_IDS.map((id) => {
                    const active = fontScale === id;
                    const label = tl.settings.appearance.fontSizes[id];
                    return (
                      <button
                        key={id}
                        type="button"
                        onClick={() => setFontScale(id)}
                        className={cn(
                          "flex flex-col items-center gap-2 rounded-md border px-3 py-3 transition-all text-center",
                          active
                            ? "border-primary bg-primary/10 ring-1 ring-primary/40"
                            : "border-border bg-secondary/30 hover:bg-secondary/60",
                        )}
                      >
                        <span
                          className={cn(
                            "font-mono font-bold leading-snug text-foreground",
                            active && "text-primary",
                          )}
                          style={{ fontSize: id === "xs" ? "0.75rem" : id === "sm" ? "0.8125rem" : id === "lg" ? "1.0625rem" : id === "xl" ? "1.125rem" : "0.875rem" }}
                        >
                          Aa
                        </span>
                        <span className="text-[10px] font-mono uppercase tracking-wide text-muted-foreground leading-tight">
                          {label}
                        </span>
                        {active && <Check className="h-3 w-3 text-primary" />}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Font style */}
              <div className="mt-6 border-t border-border pt-6">
                <div className="flex items-center gap-2 mb-4">
                  <CaseSensitive className="h-4 w-4 text-primary" />
                  <h2 className="text-sm font-bold font-mono uppercase tracking-widest text-muted-foreground">
                    {tl.settings.appearance.fontStyleLabel}
                  </h2>
                </div>
                <p className="text-xs text-muted-foreground font-mono mb-4">{tl.settings.appearance.fontStyleDesc}</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                  {FONT_STYLE_IDS.map((id) => {
                    const active = fontStyle === id;
                    const label = tl.settings.appearance.fontStyles[id];
                    const previewFamily = FONT_STYLE_PREVIEW_FAMILY[id];
                    return (
                      <button
                        key={id}
                        type="button"
                        onClick={() => setFontStyle(id)}
                        className={cn(
                          "flex flex-col items-center gap-2 rounded-md border px-3 py-3 transition-all text-center min-h-[88px]",
                          active
                            ? "border-primary bg-primary/10 ring-1 ring-primary/40"
                            : "border-border bg-secondary/30 hover:bg-secondary/60",
                        )}
                      >
                        <span
                          className={cn(
                            "font-bold leading-snug text-foreground text-lg",
                            active && "text-primary",
                          )}
                          style={id === "theme" ? undefined : { fontFamily: previewFamily }}
                        >
                          Aa
                        </span>
                        <span className="text-[10px] font-mono uppercase tracking-wide text-muted-foreground leading-tight">
                          {label}
                        </span>
                        {active && <Check className="h-3 w-3 text-primary" />}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Sidebar clock */}
              <div className="mt-6 border-t border-border pt-6 space-y-5">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="h-4 w-4 text-primary" />
                  <h2 className="text-sm font-bold font-mono uppercase tracking-widest text-muted-foreground">
                    {tl.settings.appearance.sidebarClockLabel}
                  </h2>
                </div>
                <p className="text-xs text-muted-foreground font-mono mb-2">{tl.settings.appearance.sidebarClockDesc}</p>
                <label className="flex items-start justify-between gap-4 cursor-pointer select-none">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-foreground">{tl.settings.appearance.showSidebarClockLabel}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{tl.settings.appearance.showSidebarClockDesc}</div>
                  </div>
                  <Switch
                    checked={showSidebarClock}
                    onCheckedChange={setShowSidebarClock}
                    aria-label={tl.settings.appearance.showSidebarClockLabel}
                  />
                </label>
                <label
                  className={cn(
                    "flex items-start justify-between gap-4 select-none",
                    showSidebarClock ? "cursor-pointer" : "cursor-not-allowed opacity-60",
                  )}
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-foreground">{tl.settings.appearance.localTimeLabel}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{tl.settings.appearance.localTimeDesc}</div>
                  </div>
                  <Switch
                    checked={useLocalTime}
                    onCheckedChange={setUseLocalTime}
                    disabled={!showSidebarClock}
                    aria-label={tl.settings.appearance.localTimeLabel}
                  />
                </label>
              </div>

              {/* Study comfort */}
              <div className="mt-6 border-t border-border pt-6 space-y-5">
                <label className="flex items-start justify-between gap-4 cursor-pointer select-none">
                  <div className="flex gap-3 min-w-0">
                    <Sparkles className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                    <div>
                      <div className="text-sm font-medium text-foreground">{tl.settings.appearance.reduceMotionLabel}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">{tl.settings.appearance.reduceMotionDesc}</div>
                    </div>
                  </div>
                  <Switch
                    checked={reduceMotion}
                    onCheckedChange={setReduceMotion}
                    aria-label={tl.settings.appearance.reduceMotionLabel}
                  />
                </label>
                <label className="flex items-start justify-between gap-4 cursor-pointer select-none">
                  <div className="flex gap-3 min-w-0">
                    <LayoutGrid className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                    <div>
                      <div className="text-sm font-medium text-foreground">{tl.settings.appearance.comfortableSessionLabel}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">{tl.settings.appearance.comfortableSessionDesc}</div>
                    </div>
                  </div>
                  <Switch
                    checked={comfortableSession}
                    onCheckedChange={setComfortableSession}
                    aria-label={tl.settings.appearance.comfortableSessionLabel}
                  />
                </label>
              </div>

              {/* Language section */}
              <div className="mt-6 border-t border-border pt-6">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-lg">🌐</span>
                  <h2 className="text-sm font-bold font-mono uppercase tracking-widest text-muted-foreground">
                    {tl.settings.appearance.languageLabel}
                  </h2>
                </div>
                <p className="text-xs text-muted-foreground font-mono mb-4">{tl.settings.appearance.languageDesc}</p>
                <div className="flex gap-3">
                  {(["en", "fr"] as const).map(lang => (
                    <button
                      key={lang}
                      onClick={() => setLanguage(lang)}
                      className={cn(
                        "flex items-center gap-2.5 px-5 py-3 rounded-md border text-sm font-mono font-bold transition-all",
                        language === lang
                          ? "border-primary bg-primary/10 text-primary ring-1 ring-primary/40"
                          : "border-border bg-secondary/30 text-muted-foreground hover:bg-secondary/60"
                      )}
                    >
                      <span className="text-base">{lang === "en" ? "🇨🇦" : "🇫🇷"}</span>
                      <span>{lang === "en" ? "English" : "Français"}</span>
                      {language === lang && <Check className="h-3.5 w-3.5 ml-1" />}
                    </button>
                  ))}
                </div>
              </div>

              {/* Onboarding Tour toggle */}
              <div className="mt-6 border-t border-border pt-6">
                <div className="flex items-center gap-2 mb-2">
                  <RefreshCw className="h-4 w-4 text-muted-foreground" />
                  <h2 className="text-sm font-bold font-mono uppercase tracking-widest text-muted-foreground">Onboarding Tour</h2>
                </div>
                <p className="text-xs text-muted-foreground font-mono mb-4">Enable or disable the onboarding tour.</p>
                <div className="flex items-center gap-3">
                  <Switch
                    id="onboarding-tour-toggle"
                    checked={!user?.onboardingCompleted}
                    onCheckedChange={handleTourToggle}
                    disabled={tourToggleLoading}
                  />
                  <Label htmlFor="onboarding-tour-toggle" className="font-mono text-xs text-muted-foreground cursor-pointer">
                    {user?.onboardingCompleted ? "Tour disabled" : "Tour enabled"}
                  </Label>
                  {tourToggleLoading && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ── TAB 2: ACCOUNT ── */}
        {activeTab === "account" && (
          <div className="space-y-6">
            {/* Personal Details */}
            <Card className="border border-border bg-card">
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 mb-5">
                  <User className="h-4 w-4 text-primary" />
                  <h2 className="text-sm font-bold font-mono uppercase tracking-widest text-muted-foreground">Personal Details</h2>
                </div>
                <form onSubmit={handleProfileSave} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="fullName" className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Full Name</Label>
                      <Input id="fullName" value={fullName} onChange={e => setFullName(e.target.value)} placeholder="Your full name" required className="font-mono text-sm" />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="email" className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Email</Label>
                      <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" required className="font-mono text-sm" />
                    </div>
                    <div className="space-y-1.5 sm:col-span-2">
                      <Label htmlFor="nickname" className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
                        {tl.settings.account.nicknameLabel}
                      </Label>
                      <Input
                        id="nickname"
                        value={nickname}
                        onChange={e => setNickname(e.target.value)}
                        placeholder={tl.settings.account.nicknamePlaceholder}
                        maxLength={24}
                        autoComplete="nickname"
                        className="font-mono text-sm"
                      />
                      <p className="text-[11px] text-muted-foreground leading-snug">{tl.settings.account.nicknameHint}</p>
                    </div>
                    <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-card/40 px-3 py-3 sm:col-span-2">
                      <div className="min-w-0 space-y-0.5">
                        <Label htmlFor="leaderboard-opt-in" className="text-xs font-mono uppercase tracking-widest text-muted-foreground cursor-pointer">
                          {tl.settings.account.leaderboardOptInLabel}
                        </Label>
                        <p className="text-[11px] text-muted-foreground leading-snug">{tl.settings.account.leaderboardOptInHint}</p>
                      </div>
                      <Switch
                        id="leaderboard-opt-in"
                        checked={leaderboardOptIn}
                        onCheckedChange={setLeaderboardOptIn}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="address" className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Address</Label>
                      <Input id="address" value={address} onChange={e => setAddress(e.target.value)} placeholder="Your address (optional)" className="font-mono text-sm" />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="phone" className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Phone Number</Label>
                      <Input id="phone" type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+1 (555) 000-0000 (optional)" className="font-mono text-sm" />
                    </div>
                    <div className="space-y-1.5 sm:col-span-2">
                      <Label htmlFor="country" className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Country</Label>
                      <select id="country" value={country} onChange={e => setCountry(e.target.value)}
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring">
                        <option value="">— Select country —</option>
                        {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="age" className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Age</Label>
                      <Input id="age" type="number" min={16} max={120} value={age} onChange={e => setAge(e.target.value)} placeholder="Your age (optional)" className="font-mono text-sm" />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="gender" className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Gender</Label>
                      <select id="gender" value={gender} onChange={e => setGender(e.target.value)}
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring">
                        {GENDERS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                      </select>
                    </div>
                    <div className="space-y-1.5 sm:col-span-2">
                      <Label htmlFor="pilotType" className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Pilot Licence Type</Label>
                      <select id="pilotType" value={pilotType} onChange={e => setPilotType(e.target.value)}
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring">
                        <option value="">— Select —</option>
                        {PILOT_TYPES.map(pt => <option key={pt} value={pt}>{pt}</option>)}
                      </select>
                    </div>
                  </div>

                  <div className="space-y-2 pt-1">
                    <Label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Ratings Held</Label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {RATINGS.map(r => {
                        const checked = ratings.includes(r);
                        return (
                          <button key={r} type="button"
                            onClick={() => setRatings(prev => checked ? prev.filter(x => x !== r) : [...prev, r])}
                            className={cn(
                              "text-left px-3 py-2 rounded-md border text-xs font-mono transition-all",
                              checked ? "border-primary/60 bg-primary/10 text-primary" : "border-border text-muted-foreground hover:border-primary/30 hover:text-foreground"
                            )}
                          >
                            {checked ? "▪" : "▫"} {r}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="pt-2">
                    <Button type="submit" disabled={profileLoading} className="font-mono text-xs uppercase tracking-widest">
                      {profileLoading ? <><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />Saving…</> : "Save Profile"}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            {/* Communications Preferences */}
            <Card className="border border-border bg-card">
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 mb-1">
                  <Bell className="h-4 w-4 text-primary" />
                  <h2 className="text-sm font-bold font-mono uppercase tracking-widest text-muted-foreground">Communication Preferences</h2>
                </div>
                <p className="text-xs text-muted-foreground font-mono mb-5">Choose how Flight Deck can reach you. Changes are saved automatically.</p>
                {!prefsLoaded ? (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground font-mono">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
                  </div>
                ) : (
                  <div className="space-y-4">
                    {[
                      { value: newsletterOptIn, setter: setNewsletterOptIn, key: "newsletter" as const, label: "Newsletter", desc: "Product updates, new features, and study tips from Flight Deck." },
                      { value: adminContactOptIn, setter: setAdminContactOptIn, key: "adminContact" as const, label: "Admin contact", desc: "Allow administrators to contact you for account matters, surveys, or support follow-ups." },
                    ].map(({ value, setter, key, label, desc }) => (
                      <div key={key} className="flex items-start justify-between gap-4">
                        <div>
                          <div className="text-sm font-mono font-semibold">{label}</div>
                          <div className="text-xs text-muted-foreground font-mono mt-0.5">{desc}</div>
                        </div>
                        <button
                          type="button"
                          disabled={prefsLoading}
                          onClick={async () => {
                            const newVal = !value;
                            setter(newVal);
                            await savePreferences({ [key]: newVal });
                          }}
                          className={`shrink-0 relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${value ? "bg-primary" : "bg-muted"}`}
                        >
                          <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transform transition-transform ${value ? "translate-x-4" : "translate-x-0.5"}`} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* ── TAB: SECURITY ── */}
        {activeTab === "security" && (
          <div className="space-y-6">
            {/* Change Password */}
            <Card className="border border-border bg-card">
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 mb-5">
                  <Lock className="h-4 w-4 text-primary" />
                  <h2 className="text-sm font-bold font-mono uppercase tracking-widest text-muted-foreground">Change Password</h2>
                </div>
                <form onSubmit={handlePasswordSave} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="currentPassword" className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Current Password</Label>
                    <Input id="currentPassword" type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} placeholder="Enter your current password" required className="font-mono text-sm" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="newPassword" className="text-xs font-mono uppercase tracking-widest text-muted-foreground">New Password</Label>
                    <Input id="newPassword" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="At least 8 characters" required className="font-mono text-sm" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="confirmPassword" className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Confirm New Password</Label>
                    <Input id="confirmPassword" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Repeat new password" required className="font-mono text-sm" />
                  </div>
                  <div className="pt-2">
                    <Button type="submit" disabled={passLoading} className="font-mono text-xs uppercase tracking-widest">
                      {passLoading ? <><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />Changing…</> : "Change Password"}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            {/* Security Questions */}
            <Card className="border border-border bg-card">
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 mb-1">
                  <ShieldQuestion className="h-4 w-4 text-primary" />
                  <h2 className="text-sm font-bold font-mono uppercase tracking-widest text-muted-foreground">Account Recovery Questions</h2>
                </div>
                <p className="text-xs text-muted-foreground font-mono mb-5">
                  {sqHasExisting
                    ? "Your security questions are set. Update them below — re-enter all answers to save changes."
                    : "Set 3 security questions so you can recover your account without email access."}
                </p>
                <form onSubmit={handleSecurityQuestionsSave} className="space-y-5">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="space-y-2">
                      <Label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Question {i + 1}</Label>
                      <select value={sq[i]} onChange={e => { const q = [...sq]; q[i] = e.target.value; setSq(q); }}
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring" required>
                        <option value="">— Choose a question —</option>
                        {SECURITY_QUESTIONS.filter(q => !sq.includes(q) || sq[i] === q).map(q => (
                          <option key={q} value={q}>{q}</option>
                        ))}
                      </select>
                      <Input type="text" value={sa[i]} onChange={e => { const a = [...sa]; a[i] = e.target.value; setSa(a); }}
                        placeholder={sqHasExisting ? "Enter new answer to update" : "Your answer"} required className="font-mono text-sm" autoComplete="off" />
                    </div>
                  ))}
                  <div className="pt-1">
                    <Button type="submit" disabled={sqLoading} className="font-mono text-xs uppercase tracking-widest">
                      {sqLoading ? <><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />Saving…</> : sqHasExisting ? "Update Questions" : "Save Questions"}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            {/* Two-Factor Authentication */}
            <Card id="two-factor-auth" className="border border-border bg-card">
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 mb-1">
                  <ShieldCheck className="h-4 w-4 text-primary" />
                  <h2 className="text-sm font-bold font-mono uppercase tracking-widest text-muted-foreground">Two-Factor Authentication</h2>
                </div>
                <p className="text-xs text-muted-foreground font-mono mb-5">
                  Add a second verification step when you sign in. Strongly recommended.
                </p>

                {/* Status badge */}
                <div className="mb-5">
                  {twoFaMethod === "totp" && (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-mono font-semibold bg-primary/15 text-primary border border-primary/30">
                      <Smartphone className="w-3 h-3" /> Authenticator app enabled
                    </span>
                  )}
                  {twoFaMethod === "email" && (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-mono font-semibold bg-primary/15 text-primary border border-primary/30">
                      <Mail className="w-3 h-3" /> Email OTP enabled
                    </span>
                  )}
                  {!twoFaMethod && !totpSetupPending && (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-mono font-semibold bg-muted text-muted-foreground border border-border">
                      Off
                    </span>
                  )}
                </div>

                {/* Recovery codes section — shown when 2FA is active */}
                {twoFaMethod && !totpSetupPending && (
                  <div className="flex items-center justify-between bg-muted/40 border border-border rounded-md px-4 py-3 mb-5">
                    <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground">
                      <KeyRound className="w-3.5 h-3.5 text-primary/70 shrink-0" />
                      <span>
                        Recovery codes:{" "}
                        <span className={cn(
                          "font-semibold",
                          recoveryCodesRemaining <= 2 ? "text-amber-400" : "text-foreground"
                        )}>
                          {recoveryCodesRemaining} remaining
                        </span>
                        {recoveryCodesRemaining <= 2 && (
                          <span className="ml-1 text-amber-400">— regenerate soon</span>
                        )}
                      </span>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={regenerateRecoveryCodes}
                      disabled={regenLoading}
                      className="font-mono text-xs uppercase tracking-widest gap-1.5 h-7"
                    >
                      {regenLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                      {regenLoading ? "Generating…" : "Regenerate"}
                    </Button>
                  </div>
                )}

                {/* TOTP setup in progress */}
                {totpSetupPending && twoFaQrDataUrl && (
                  <div className="space-y-4 mb-5">
                    <p className="text-xs font-mono text-muted-foreground">
                      Scan this QR code with your authenticator app (e.g. Google Authenticator, Authy, 1Password), then enter the 6-digit code below to confirm.
                    </p>
                    <div className="flex justify-center">
                      <img src={twoFaQrDataUrl} alt="TOTP QR code" className="w-40 h-40 rounded border border-border bg-white p-1" />
                    </div>
                    <div>
                      <Label className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1 block">Manual entry key</Label>
                      <div className="flex items-center gap-2">
                        <code className={`flex-1 text-xs font-mono bg-muted px-3 py-2 rounded border border-border break-all ${showTotpSecret ? "" : "select-none tracking-widest text-2xl"}`}>
                          {showTotpSecret ? twoFaTotpSecret : "••••••••••••••••"}
                        </code>
                        <button
                          onClick={() => setShowTotpSecret(v => !v)}
                          className="text-muted-foreground hover:text-foreground transition-colors"
                          title={showTotpSecret ? "Hide" : "Show"}
                        >
                          {showTotpSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1 block">Verification code</Label>
                      <Input
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        value={twoFaVerifyCode}
                        onChange={e => setTwoFaVerifyCode(e.target.value.replace(/\D/g, ""))}
                        placeholder="000000"
                        className="font-mono tracking-widest text-lg w-40"
                        autoComplete="one-time-code"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={verifyTotpSetup}
                        disabled={twoFaLoading || twoFaVerifyCode.length < 6}
                        className="font-mono text-xs uppercase tracking-widest"
                      >
                        {twoFaLoading ? <><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />Verifying…</> : "Confirm & Enable"}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={cancelTotpSetup}
                        disabled={twoFaLoading}
                        className="font-mono text-xs uppercase tracking-widest"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}

                {/* Enable options — shown when 2FA is off */}
                {!twoFaMethod && !totpSetupPending && (
                  <div className="flex flex-col sm:flex-row gap-2 mb-5">
                    <Button
                      onClick={startTotpSetup}
                      disabled={twoFaLoading}
                      variant="outline"
                      className="font-mono text-xs uppercase tracking-widest flex items-center gap-2"
                    >
                      <Smartphone className="w-3.5 h-3.5" />
                      {twoFaLoading ? "Starting…" : "Setup Authenticator App"}
                    </Button>
                    <Button
                      onClick={enableEmailOtp}
                      disabled={twoFaLoading}
                      variant="outline"
                      className="font-mono text-xs uppercase tracking-widest flex items-center gap-2"
                    >
                      <Mail className="w-3.5 h-3.5" />
                      {twoFaLoading ? "Enabling…" : "Enable Email OTP"}
                    </Button>
                  </div>
                )}

                {/* Disable 2FA section — shown when active */}
                {twoFaMethod && (
                  <div className="space-y-3">
                    {!showTwoFaDisable ? (
                      <Button
                        variant="outline"
                        onClick={() => setShowTwoFaDisable(true)}
                        className="font-mono text-xs uppercase tracking-widest text-destructive border-destructive/40 hover:bg-destructive/10"
                      >
                        Disable 2FA
                      </Button>
                    ) : (
                      <div className="space-y-3 border border-destructive/20 rounded-md p-4 bg-destructive/5">
                        <p className="text-xs font-mono text-muted-foreground">Enter your current password to disable 2FA:</p>
                        <Input
                          type="password"
                          value={twoFaDisablePassword}
                          onChange={e => setTwoFaDisablePassword(e.target.value)}
                          placeholder="Current password"
                          className="font-mono text-sm"
                          autoComplete="current-password"
                        />
                        <div className="flex gap-2">
                          <Button
                            onClick={disable2FA}
                            disabled={twoFaLoading || !twoFaDisablePassword.trim()}
                            variant="destructive"
                            className="font-mono text-xs uppercase tracking-widest"
                          >
                            {twoFaLoading ? <><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />Disabling…</> : "Confirm Disable"}
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => { setShowTwoFaDisable(false); setTwoFaDisablePassword(""); }}
                            className="font-mono text-xs uppercase tracking-widest"
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Account Management */}
            <Card className="border border-destructive/30 bg-card">
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 mb-1">
                  <Trash2 className="h-4 w-4 text-destructive" />
                  <h2 className="text-sm font-bold font-mono uppercase tracking-widest text-muted-foreground">Account Management</h2>
                </div>
                <p className="text-xs text-muted-foreground font-mono mb-5">Temporarily pause or permanently remove your account.</p>

                <div className="space-y-4">
                  {/* Pause / Resume */}
                  <div className="flex items-start justify-between gap-4 border border-border rounded-lg p-4">
                    <div>
                      <div className="text-sm font-mono font-semibold flex items-center gap-2">
                        {acctStatus?.paused
                          ? <><PauseCircle className="h-4 w-4 text-amber-400" /> Account paused</>
                          : <><PauseCircle className="h-4 w-4 text-amber-500" /> Pause account</>}
                      </div>
                      <div className="text-xs text-muted-foreground font-mono mt-1 max-w-xs">
                        {acctStatus?.paused
                          ? <>
                              {acctStatus.pauseExpiresAt
                                ? `Paused until ${new Date(acctStatus.pauseExpiresAt).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}.`
                                : "Your account is currently paused."}
                              {" "}Practice and Turbulence Zones are unavailable. You can resume anytime.
                            </>
                          : "Pause your subscription for up to 30 days without losing your progress. Your Stripe subscription will be frozen."}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant={acctStatus?.paused ? "default" : "outline"}
                      disabled={acctLoading === "pause" || acctLoading === "resume"}
                      onClick={acctStatus?.paused ? resumeAccount : () => setShowPauseConfirm(true)}
                      className="shrink-0 font-mono text-xs"
                    >
                      {(acctLoading === "pause" || acctLoading === "resume")
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        : acctStatus?.paused
                          ? <><PlayCircle className="h-3.5 w-3.5 mr-1" />Resume</>
                          : "Pause"}
                    </Button>
                  </div>

                  {/* Reset Stats */}
                  <div className="border border-orange-500/30 rounded-lg p-4 space-y-3">
                    <div className="text-sm font-mono font-semibold text-orange-400 flex items-center gap-2">
                      <RefreshCw className="h-4 w-4" /> Reset stats &amp; flight logbook
                    </div>
                    <div className="text-xs text-muted-foreground font-mono">
                      Permanently erase all practice sessions, answers, and flight log entries. Your account, subscription, and questions are not affected.
                    </div>
                    {!showResetConfirm ? (
                      <Button size="sm" variant="outline" className="font-mono text-xs border-orange-500/40 text-orange-400 hover:bg-orange-500/10"
                        onClick={() => setShowResetConfirm(true)}>
                        Reset stats
                      </Button>
                    ) : (
                      <div className="space-y-2">
                        <Label className="text-xs font-mono text-muted-foreground">Type <span className="font-bold text-orange-400">RESET</span> to confirm</Label>
                        <Input
                          value={resetConfirmText}
                          onChange={e => setResetConfirmText(e.target.value)}
                          placeholder="RESET"
                          className="font-mono text-xs h-8 max-w-[180px]"
                        />
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" className="font-mono text-xs border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                            disabled={resetConfirmText !== "RESET" || acctLoading === "reset"}
                            onClick={resetStats}>
                            {acctLoading === "reset" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Confirm reset"}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => { setShowResetConfirm(false); setResetConfirmText(""); }}
                            className="font-mono text-xs">Cancel</Button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Delete */}
                  <div className="border border-destructive/30 rounded-lg p-4 space-y-3">
                    <div className="text-sm font-mono font-semibold text-destructive flex items-center gap-2">
                      <Trash2 className="h-4 w-4" /> Delete account
                    </div>
                    <div className="text-xs text-muted-foreground font-mono">
                      Permanently deactivate your account and cancel all active subscriptions. This action cannot be undone.
                    </div>
                    {!showDeleteConfirm ? (
                      <Button size="sm" variant="destructive" className="font-mono text-xs"
                        onClick={() => setShowDeleteConfirm(true)}>
                        Delete my account
                      </Button>
                    ) : (
                      <div className="space-y-2">
                        <Label className="text-xs font-mono text-muted-foreground">Type <span className="font-bold text-destructive">DELETE</span> to confirm</Label>
                        <Input
                          value={deleteConfirmText}
                          onChange={e => setDeleteConfirmText(e.target.value)}
                          placeholder="DELETE"
                          className="font-mono text-sm border-destructive/50 max-w-xs"
                        />
                        <div className="flex gap-2 pt-1">
                          <Button size="sm" variant="destructive" disabled={deleteConfirmText !== "DELETE" || acctLoading === "delete"}
                            onClick={deleteAccount} className="font-mono text-xs">
                            {acctLoading === "delete" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Confirm delete"}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText(""); }}
                            className="font-mono text-xs">
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ── TAB 3: BILLING ── */}
        {activeTab === "billing" && (
          <div className="space-y-5">
            {billingLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                {/* Current plan card */}
                <Card className="border border-border bg-card">
                  <CardContent className="pt-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CreditCard className="h-4 w-4 text-primary" />
                        <h2 className="text-sm font-bold font-mono uppercase tracking-widest text-muted-foreground">Current Plan — CPL Track</h2>
                      </div>
                      <button onClick={loadBilling} title="Refresh" className="text-muted-foreground/50 hover:text-muted-foreground transition-colors">
                        <RefreshCw className="h-3.5 w-3.5" />
                      </button>
                    </div>

                    <div className="flex items-center gap-3">
                      <PlanBadge plan={billingPlan} />
                      {subDetails && (
                        <span className={cn(
                          "text-xs font-mono px-2 py-0.5 rounded-full border",
                          subDetails.status === "active" ? "bg-green-500/10 text-green-400 border-green-500/30" :
                          subDetails.status === "past_due" ? "bg-red-500/10 text-red-400 border-red-500/30" :
                          "bg-secondary text-muted-foreground border-border"
                        )}>
                          {subDetails.status.toUpperCase().replace("_", " ")}
                        </span>
                      )}
                    </div>

                    {/* Subscription details */}
                    {subDetails ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                        <div className="bg-secondary/30 rounded-md px-4 py-3 space-y-0.5">
                          <div className="text-xs text-muted-foreground font-mono uppercase tracking-widest">Billing Cycle</div>
                          <div className="font-mono text-foreground capitalize">
                            {subDetails.interval === "year" ? "Annual" : "Monthly"}
                            {subDetails.amount != null && (
                              <span className="text-muted-foreground ml-2">
                                — {(subDetails.amount / 100).toLocaleString("en-CA", { style: "currency", currency: subDetails.currency.toUpperCase() })}
                                /{subDetails.interval === "year" ? "yr" : "mo"}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="bg-secondary/30 rounded-md px-4 py-3 space-y-0.5">
                          <div className="text-xs text-muted-foreground font-mono uppercase tracking-widest">
                            {subDetails.cancelAtPeriodEnd ? "Access Until" : "Next Renewal"}
                          </div>
                          <div className="font-mono text-foreground">
                            {new Date(subDetails.currentPeriodEnd * 1000).toLocaleDateString("en-CA", {
                              year: "numeric", month: "long", day: "numeric",
                            })}
                          </div>
                        </div>
                        {subDetails.cancelAtPeriodEnd && (
                          <div className="sm:col-span-2 flex items-start gap-2 bg-amber-500/10 border border-amber-500/30 rounded-md px-4 py-3">
                            <AlertCircle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
                            <p className="text-xs font-mono text-amber-300">
                              Your subscription is cancelled and will not renew. You keep full access until the date above.
                              Reactivate via the billing portal below.
                            </p>
                          </div>
                        )}
                      </div>
                    ) : billingPlan === "free" ? (
                      <p className="text-sm text-muted-foreground font-mono">
                        You're on the Basic (Free) plan. Upgrade below to access more questions and features.
                      </p>
                    ) : null}

                    {/* Manage billing (for paying customers) */}
                    {billingPlan !== "free" && (
                      <div className="pt-1">
                        <Button variant="outline" onClick={openPortal} disabled={portalLoading}
                          className="font-mono text-xs uppercase tracking-widest gap-2">
                          {portalLoading
                            ? <><Loader2 className="h-3.5 w-3.5 animate-spin" />Opening…</>
                            : <><ExternalLink className="h-3.5 w-3.5" />Manage Billing</>}
                        </Button>
                        <p className="text-[11px] text-muted-foreground font-mono mt-2">
                          Update payment method, download invoices, or cancel your subscription.
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Upgrade / plan options */}
                {billingPlan === "free" && (
                  <div className="space-y-3">
                    <h3 className="text-sm font-bold font-mono uppercase tracking-widest text-muted-foreground px-1">Upgrade Your Plan</h3>

                    {/* Pro */}
                    <Card className="border border-primary/30 bg-card">
                      <CardContent className="pt-5 space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Zap className="h-4 w-4 text-primary" />
                            <span className="font-bold font-mono text-primary uppercase tracking-widest text-sm">Pro</span>
                          </div>
                          <span className="text-muted-foreground font-mono text-xs">CA$14.99/mo · CA$149/yr</span>
                        </div>
                        <ul className="text-xs text-muted-foreground font-mono space-y-1">
                          <li>✓ Everything in Basic +</li>
                          <li>✓ Up to 400 questions (non-rotational pool)</li>
                          <li>✓ All sections unlocked</li>
                          <li>✓ Basic progress tracking</li>
                          <li>✗ Exam simulations (Pro Plus only)</li>
                        </ul>
                        <div className="flex gap-2 pt-1">
                          <Button size="sm" onClick={() => startCheckout("pro", "month")} disabled={!!checkoutLoading}
                            className="font-mono text-xs uppercase tracking-widest">
                            {checkoutLoading === "pro-month" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Monthly — $14.99"}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => startCheckout("pro", "year")} disabled={!!checkoutLoading}
                            className="font-mono text-xs uppercase tracking-widest border-primary/40 text-primary hover:bg-primary/10">
                            {checkoutLoading === "pro-year" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Annual — $149 (save 17%)"}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Pro Plus */}
                    <Card className="border border-amber-500/30 bg-card">
                      <CardContent className="pt-5 space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Crown className="h-4 w-4 text-amber-400" />
                            <span className="font-bold font-mono text-amber-400 uppercase tracking-widest text-sm">Pro Plus</span>
                          </div>
                          <span className="text-muted-foreground font-mono text-xs">CA$24.99/mo · CA$249/yr</span>
                        </div>
                        <ul className="text-xs text-muted-foreground font-mono space-y-1">
                          <li>✓ Everything in Pro +</li>
                          <li>✓ Full question bank — 1,400+ questions (rotating)</li>
                          <li>✓ Exam simulations &amp; weakness training</li>
                          <li>✓ Priority support</li>
                        </ul>
                        <div className="flex gap-2 pt-1">
                          <Button size="sm" onClick={() => startCheckout("pro_plus", "month")} disabled={!!checkoutLoading}
                            className="font-mono text-xs uppercase tracking-widest bg-amber-500 hover:bg-amber-600 text-black border-0">
                            {checkoutLoading === "pro_plus-month" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Monthly — $24.99"}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => startCheckout("pro_plus", "year")} disabled={!!checkoutLoading}
                            className="font-mono text-xs uppercase tracking-widest border-amber-500/40 text-amber-400 hover:bg-amber-500/10">
                            {checkoutLoading === "pro_plus-year" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Annual — $249 (save 17%)"}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Downgrade option for paid users */}
                {billingPlan !== "free" && (
                  <Card className="border border-border bg-card">
                    <CardContent className="pt-5 space-y-2">
                      <h3 className="text-sm font-bold font-mono uppercase tracking-widest text-muted-foreground">Change or Cancel Plan</h3>
                      <p className="text-xs text-muted-foreground font-mono">
                        To switch plans, update your payment method, or cancel your subscription, use the billing portal. Your access continues until the end of your current billing period.
                      </p>
                      <Button variant="outline" size="sm" onClick={openPortal} disabled={portalLoading}
                        className="font-mono text-xs uppercase tracking-widest gap-2 mt-2">
                        {portalLoading
                          ? <><Loader2 className="h-3.5 w-3.5 animate-spin" />Opening…</>
                          : <><ExternalLink className="h-3.5 w-3.5" />Open Billing Portal</>}
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </Layout>

    {/* ── Pause account confirmation dialog ── */}
    <Dialog open={showPauseConfirm} onOpenChange={setShowPauseConfirm}>
      <DialogContent className="max-w-md font-mono">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <PauseCircle className="h-5 w-5 text-amber-400" />
            Pause your account?
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-1">
          {/* Main summary */}
          <p className="text-sm text-muted-foreground leading-relaxed">
            Pausing freezes your study access and extends your next billing date by the exact number of days you're paused. You can resume anytime.
          </p>

          {/* Rules */}
          <div className="rounded-lg border border-border bg-muted/30 divide-y divide-border overflow-hidden">
            <div className="flex items-start gap-3 px-4 py-3">
              <Calendar className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
              <div>
                <div className="text-xs font-semibold text-foreground">Max duration</div>
                <div className="text-xs text-muted-foreground mt-0.5">Up to 30 days per pause</div>
              </div>
            </div>
            <div className="flex items-start gap-3 px-4 py-3">
              <Timer className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
              <div>
                <div className="text-xs font-semibold text-foreground">Limit per year</div>
                <div className="text-xs text-muted-foreground mt-0.5">Maximum 2 pauses per calendar year</div>
              </div>
            </div>
            <div className="flex items-start gap-3 px-4 py-3">
              <Clock className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
              <div>
                <div className="text-xs font-semibold text-foreground">Cooldown</div>
                <div className="text-xs text-muted-foreground mt-0.5">30-day cooldown between pauses · Must be active for 7 days before pausing</div>
              </div>
            </div>
            <div className="flex items-start gap-3 px-4 py-3">
              <ShieldAlert className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
              <div>
                <div className="text-xs font-semibold text-foreground">Restrictions</div>
                <div className="text-xs text-muted-foreground mt-0.5">Cannot pause within 48 hours of your renewal date · Active subscribers only</div>
              </div>
            </div>
            <div className="flex items-start gap-3 px-4 py-3">
              <CreditCard className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
              <div>
                <div className="text-xs font-semibold text-foreground">Billing</div>
                <div className="text-xs text-muted-foreground mt-0.5">Your subscription continues. Billing date shifts forward by the days paused — no charge during pause.</div>
              </div>
            </div>
          </div>

          {/* Blocked features callout */}
          <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2.5">
            <Info className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-200/80">
              While paused, <span className="font-semibold text-amber-300">Request Takeoff</span> and <span className="font-semibold text-amber-300">Turbulence Zones</span> will be unavailable. All other areas remain accessible.
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" size="sm" className="font-mono text-xs" onClick={() => setShowPauseConfirm(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            className="font-mono text-xs"
            disabled={acctLoading === "pause"}
            onClick={() => { setShowPauseConfirm(false); pauseAccount(); }}
          >
            {acctLoading === "pause" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><PauseCircle className="h-3.5 w-3.5 mr-1.5" />Confirm Pause</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
